import { Doc } from '@dadohr/shared/domainTypes/document';
import { EMPTY_ARR, NOOP } from '@dadohr/shared/domainTypes/emptyConstants';
import {
  IEmployeeAppInboxMessage,
  IEmployeeAppInboxResponse,
  IEmployeeAppInboxTask,
  IEmployeeAppPinnedItemsResponse,
  IExperienceDenormalizedV2,
  IMomentDenormalized,
  PARTICIPANT_ACTOR_KEY,
  TaskStatus
} from '@dadohr/shared/domainTypes/experience';
import { PAEventTypeV2 } from '@dadohr/shared/domainTypes/productAnalytics';
import { IUserDenormalized } from '@dadohr/shared/domainTypes/user';
import {
  Button,
  CircularProgress,
  MenuItem,
  Select,
  Typography
} from '@material-ui/core';
import { uniq } from 'lodash';
import { CircleArrowLeft } from 'lucide-react';
import { Fragment, useEffect, useMemo, useRef } from 'react';
import ReactHelmet from 'react-helmet';
import { Link } from 'react-router-dom';
import { SomethingWentWrongWithReport } from '../../../../../../components/SomethingWentWrong';
import { useErrorLogger } from '../../../../../../hooks/useErrorLogger';
import { useRouter } from '../../../../../../hooks/useRouter';
import { useUnsafeTranslation } from '../../../../../../i18next';
import { Centered } from '../../../../../../layout/Centered';
import {
  FlexContainer,
  FlexContainerVertical
} from '../../../../../../layout/Flex';
import {
  useNullableTypedStringQueryParam,
  useStringQueryParam,
  useTypedStringQueryParam
} from '../../../../../../routes';
import { LoadingValue } from '../../../../../../services/firebase';
import { toFullName } from '../../../../../../services/user';
import { useProductAnalyticsV2 } from '../../../../../features/Experience/services/productAnalytics';
import { useStandaloneAppThemeOverride } from '../../../../../hooks/useStandaloneAppContext';
import { ROUTES_V2 } from '../../../../../routes';
import { WithBreakpoints } from '../../components/Breakpoints';
import {
  EmployeeAppLoader,
  EmployeeAppPageLoader
} from '../../components/EmployeeAppLoader';
import {
  InboxNavItemKey,
  NavBar,
  NavBarItem,
  NavContainer,
  NavDropdown,
  NavItemContainer
} from '../../components/Nav/InboxNav';
import {
  PageContainer,
  PageContainerInner,
  PageFrame,
  PageFrameHeader
} from '../../components/PageFrame';
import {
  InstancePreloadSink,
  MomentPreloadSink
} from '../../components/PreloadSinks';
import { SearchWithinInboxButton } from '../../components/Search/WithinInbox';
import { DueOrCompletionDate, PlainDate } from '../../components/Tasks';
import { TopNavBarV2ForExperienceHub } from '../../components/TopNav';
import { getAccompanyingMessage } from '../../services/items';
import {
  useEmployeeAppExperienceInstance,
  useEmployeeAppInboxDataAndPinnedItemsAndUpdateDate,
  useEmployeeAppMomentInstance
} from '../../services/retrieval';
import { getInboxUrl } from '../../services/routes';
import {
  InboxFilters,
  useFilterOptionsForInbox,
  useSelectedTemplatesForInbox,
  useSelectedUsersForInbox
} from './components/FilterTools';
import { InboxMessageDetails } from './components/MessageDetails';
import { MessageGroupTable } from './components/MessageGroupTable';
import { InboxMomentDetails } from './components/MomentDetails';
import { MomentsArchivedTable, MomentsTable } from './components/MomentsTable';
import { PinButton } from './components/PinButtons';
import { InboxTaskDetails } from './components/TaskDetails';
import {
  TaskGroupTable,
  TASK_GROUP_TABLE_SORT_QUERY_PARAM
} from './components/TaskGroupTable';
import { WithTokenPill } from './components/WithTokenPill';
import {
  groupMessages,
  groupTasks,
  useGroupedInboxData,
  useNavGroups
} from './groups';
import {
  sortMessages,
  sortTasks,
  useNextButtonDefForInboxMessage,
  useNextButtonDefForInboxMoment,
  useNextButtonDefForInboxTask
} from './nextButtons';
import { InboxTableMode, TemplateOption } from './types';

export const useTrackCombinedInboxPageLoadedEvent = (
  userAccessToken: string
) => {
  const { trackEventV2 } = useProductAnalyticsV2(userAccessToken);
  useEffect(() => {
    trackEventV2({
      eventType: PAEventTypeV2.OTHER,
      name: 'Combined Inbox Page Loaded',
      Explanation:
        'A user opened the the combined inbox page of the employee app'
    });
  }, [trackEventV2]);
};

const Loader = ({ height }: { height: number }) => {
  return (
    <Centered height={height}>
      <EmployeeAppLoader height={Math.min(200, (height / 3) * 2)} />
    </Centered>
  );
};

const RefetchIndicator = ({ active }: { active: boolean }) => {
  const { t } = useUnsafeTranslation();
  if (!active) {
    return null;
  }
  return (
    <FlexContainer spacing={0.5}>
      <CircularProgress color="primary" size={12} />
      <Typography variant="caption">{t('Updating data...')}</Typography>
    </FlexContainer>
  );
};

const useUserFilteredInboxData = (
  data: IEmployeeAppInboxResponse,
  selectedUsers: string[] | null,
  selectedTemplates: Set<string>
) => {
  return useMemo<IEmployeeAppInboxResponse>(() => {
    const isSelected = <
      X extends { participant: IUserDenormalized } & TemplateOption
    >(
      x: X
    ) =>
      (!selectedUsers ||
        !selectedUsers.length ||
        selectedUsers.includes(x.participant.publicId)) &&
      (!selectedTemplates.size || selectedTemplates.has(x.templateId));

    return {
      ...data,
      tasks: data.tasks.filter(isSelected),
      messages: data.messages.filter(isSelected),
      moments:
        (selectedUsers || []).length || selectedTemplates.size
          ? EMPTY_ARR
          : data.moments
    };
  }, [data, selectedUsers, selectedTemplates]);
};

const NavStyleDropdown = ({
  data,
  pinnedItems,
  g,
  u,
  t,
  onNavItemClick,
  topActions
}: {
  data: IEmployeeAppInboxResponse;
  pinnedItems: IEmployeeAppPinnedItemsResponse['items'];
  g: string;
  u: string[];
  t: Set<string>;
  onNavItemClick: (item: NavBarItem<InboxNavItemKey>) => void;
  topActions?: React.ReactNode;
}) => {
  const groups = useNavGroups(data, pinnedItems, g, 'dropdown', u, t);
  return (
    <NavDropdown
      groups={groups}
      onNavItemClick={onNavItemClick}
      topActions={topActions}
    />
  );
};

const NavStyleBar = ({
  data,
  pinnedItems,
  g,
  u,
  t,
  onNavItemClick,
  topActions
}: {
  data: IEmployeeAppInboxResponse;
  pinnedItems: IEmployeeAppPinnedItemsResponse['items'];
  g: string;
  u: string[];
  t: Set<string>;
  onNavItemClick: (item: NavBarItem<InboxNavItemKey>) => void;
  topActions?: React.ReactNode;
}) => {
  const groups = useNavGroups(data, pinnedItems, g, 'bar', u, t);
  return (
    <NavBar
      groups={groups}
      onNavItemClick={onNavItemClick}
      topActions={topActions}
    />
  );
};

const Nav = (props: {
  data: IEmployeeAppInboxResponse;
  pinnedItems: IEmployeeAppPinnedItemsResponse['items'];
  g: string;
  u: string[];
  t: Set<string>;
  topActions?: React.ReactNode;
}) => {
  const { trackEventV2 } = useProductAnalyticsV2(props.data.userAccessToken);
  const onNavItemClick = (item: NavBarItem<InboxNavItemKey>) => {
    trackEventV2({
      eventType: PAEventTypeV2.OTHER,
      name: 'Status Navigation Clicked',
      Explanation: 'User clicked on a section in the status navigation',
      Status: item.trackingKey,
      'Item Count': item.count,
      'Filters Applied': props.u.length > 0 || props.t.size > 0
    });
  };
  return (
    <WithBreakpoints
      lgMd={<NavStyleBar {...props} onNavItemClick={onNavItemClick} />}
      smXs={<NavStyleDropdown {...props} onNavItemClick={onNavItemClick} />}
    />
  );
};

const determineMode = <
  X extends { actorKey: string; participant: { publicId: string } }
>(
  xs: X[]
): InboxTableMode => {
  return xs.every((x) => x.actorKey === PARTICIPANT_ACTOR_KEY)
    ? 'participantOnly'
    : uniq(xs.map((x) => x.participant.publicId)).length === 1
    ? 'singleUser'
    : 'withSupportingActor';
};

const TaskDetails = ({
  data,
  taskTemplateId,
  instance,
  returnTo,
  noBackButton = false,
  showTemplateTitle = false,
  additionalLinkInHeader = null,
  nextButtons = EMPTY_ARR
}: {
  data: IEmployeeAppInboxResponse;
  taskTemplateId: string;
  instance: Doc<IExperienceDenormalizedV2>;
  returnTo?: string;
  noBackButton?: boolean;
  showTemplateTitle?: boolean;
  additionalLinkInHeader?: {
    label: string;
    url: string;
  } | null;
  nextButtons?: {
    label: string;
    url: string;
    variant?: 'outlined' | 'contained';
  }[];
}) => {
  const { t } = useUnsafeTranslation();
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    // The parent element is the scroll container. When we move between tasks, go back to the top
    ref.current?.parentElement?.scrollTo(0, 0);
  }, [taskTemplateId]);

  const task = useMemo(() => {
    return instance.data.tasks.find((tsk) => tsk.templateId === taskTemplateId);
  }, [instance.data.tasks, taskTemplateId]);

  const accompanyingMessage = useMemo(
    () => getAccompanyingMessage(instance.data.messages, task),
    [instance.data.messages, task]
  );

  if (!task) {
    return (
      <Centered height={300}>
        <Typography variant="caption">{t('Task not found')}</Typography>
      </Centered>
    );
  }

  return (
    <InboxTaskDetails
      instance={instance}
      task={task}
      actorAccessToken={data.userAccessToken}
      instanceAccessToken={instance.data.accessToken}
      returnTo={returnTo}
      accompanyingMessage={accompanyingMessage}
      showTemplateTitle={showTemplateTitle}
      noBackButton={noBackButton}
      nextButtons={nextButtons}
      additionalLinkInHeader={additionalLinkInHeader}
    />
  );
};

const MessageDetails = ({
  data,
  msgTemplateId,
  instance,
  returnTo,
  noBackButton = false,
  showTemplateTitle = false,
  additionalLinkInHeader = null,
  nextButtons = EMPTY_ARR
}: {
  data: IEmployeeAppInboxResponse;
  msgTemplateId: string;
  instance: Doc<IExperienceDenormalizedV2>;
  returnTo?: string;
  noBackButton?: boolean;
  showTemplateTitle?: boolean;
  additionalLinkInHeader?: {
    label: string;
    url: string;
  } | null;
  nextButtons?: {
    label: string;
    url: string;
  }[];
}) => {
  const { t } = useUnsafeTranslation();
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    // The parent element is the scroll container. When we move between tasks, go back to the top
    ref.current?.parentElement?.scrollTo(0, 0);
  }, [msgTemplateId]);

  const message = useMemo(() => {
    return instance.data.messages.find(
      (msg) => msg.templateId === msgTemplateId
    );
  }, [instance.data.messages, msgTemplateId]);

  if (!message) {
    return (
      <Centered height={300}>
        <Typography variant="caption">{t('Message not found')}</Typography>
      </Centered>
    );
  }

  return (
    <InboxMessageDetails
      instance={instance}
      message={message}
      actorAccessToken={data.userAccessToken}
      instanceAccessToken={instance.data.accessToken}
      returnTo={returnTo}
      showTemplateTitle={showTemplateTitle}
      noBackButton={noBackButton}
      nextButtons={nextButtons}
      additionalLinkInHeader={additionalLinkInHeader}
    />
  );
};

const InboxMain = ({
  data,
  pinnedItems,
  g,
  setSelectedGroup,
  u,
  t,
  returnTo,
  linkToOverdueSection,
  tools,
  mode
}: {
  data: IEmployeeAppInboxResponse;
  pinnedItems: IEmployeeAppPinnedItemsResponse['items'];
  g: InboxNavItemKey;
  setSelectedGroup: (nextValue: InboxNavItemKey | null) => void;
  u?: string[] | null;
  t?: string[] | null;
  returnTo: string;
  linkToOverdueSection: string;
  tools?: React.ReactNode;
  mode: InboxTableMode;
}) => {
  const {
    taskGroups,
    messageGroups,
    moments,
    title,
    emptyState
  } = useGroupedInboxData(data, pinnedItems, g, linkToOverdueSection);

  const isEmpty =
    !taskGroups.length &&
    !messageGroups.length &&
    !moments.length &&
    // Special group, which makes an additional request, only then we can determine if we show the empty state
    g !== 'moments-archived';

  if (g === 'pinned-tasks' && !pinnedItems.length) {
    setSelectedGroup('everything');
    return null;
  }

  // If selected group is pinned and there are no pinned items whatsoever, then
  // redirect the group to the everything group

  return (
    <FlexContainerVertical fullWidth spacing={2} marginBottom={3}>
      <FlexContainer fullWidth justifyContent="space-between">
        <Typography variant="h3">{title}</Typography>
        <FlexContainer justifyContent="flex-end">{tools}</FlexContainer>
      </FlexContainer>
      {isEmpty && emptyState}
      {!!taskGroups.length && (
        <TaskGroupTable
          groups={taskGroups}
          mode={mode}
          userAccessToken={data.userAccessToken}
          returnTo={returnTo}
          selectedGroup={g}
          selectedTemplates={t}
          selectedUsers={u}
          highlightOverdue
          includePinColumn={g === 'pinned-tasks'}
        />
      )}
      {!!messageGroups.length && (
        <MessageGroupTable
          groups={messageGroups}
          mode={mode}
          userAccessToken={data.userAccessToken}
          returnTo={returnTo}
          selectedGroup={g}
          selectedTemplates={t}
          selectedUsers={u}
        />
      )}

      {!!moments.length && g !== 'moments-archived' && (
        <MomentsTable
          moments={moments}
          userAccessToken={data.userAccessToken}
          returnTo={returnTo}
          selectedGroup={g}
        />
      )}
      {g === 'moments-archived' && (
        <MomentsArchivedTable
          moments={moments}
          userAccessToken={data.userAccessToken}
          returnTo={returnTo}
          selectedGroup={g}
          emptyState={emptyState}
        />
      )}
    </FlexContainerVertical>
  );
};

const EmployeeAppPageInboxWithData = ({
  data,
  pinnedItems,
  refetchInProgress
}: {
  data: IEmployeeAppInboxResponse;
  pinnedItems: IEmployeeAppPinnedItemsResponse['items'];
  refetchInProgress: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  useStandaloneAppThemeOverride(data.org.experienceTheming || null);
  useTrackCombinedInboxPageLoadedEvent(data.userAccessToken);
  const { trackEventV2 } = useProductAnalyticsV2(data.userAccessToken);

  const [
    selectedGroup,
    setSelectedGroup
  ] = useTypedStringQueryParam<InboxNavItemKey>('g', 'everything');
  // Get all unique templates here
  // Get also all unique users here
  // then filter the response down
  // pass the filtered response down to the individual components
  // add all options and callbacks to the InboxMain component, or even better
  // pass the components directly down
  const { userOptions, tplOptions } = useFilterOptionsForInbox(data);
  const [selectedUsers, setSelectedUsers] = useSelectedUsersForInbox();
  const [
    selectedTemplates,
    setSelectedTemplates
  ] = useSelectedTemplatesForInbox();
  const filteredData = useUserFilteredInboxData(
    data,
    selectedUsers,
    selectedTemplates
  );

  const mode = useMemo(() => {
    return determineMode([...filteredData.tasks, ...filteredData.messages]);
  }, [filteredData]);

  const [tableSortParam] = useStringQueryParam(
    TASK_GROUP_TABLE_SORT_QUERY_PARAM,
    ''
  );

  const selectedTemplatesArray = useMemo(() => {
    return [...selectedTemplates];
  }, [selectedTemplates]);

  const returnTo = getInboxUrl(data.userAccessToken, {
    g: selectedGroup,
    u: selectedUsers,
    t: selectedTemplatesArray,
    tsort: tableSortParam
  });

  const linkToOverdueSection = getInboxUrl(data.userAccessToken, {
    g: 'overdue',
    u: selectedUsers,
    t: selectedTemplatesArray,
    tsort: tableSortParam
  });

  return (
    <>
      <ReactHelmet>
        <title>{t('Inbox')}</title>
      </ReactHelmet>

      <PageContainer>
        <PageContainerInner>
          <TopNavBarV2ForExperienceHub
            data={data}
            searchComponent={<SearchWithinInboxButton data={data} />}
          />
          <PageFrame
            aiDisclosure={data.aiDisclosure}
            header={
              <PageFrameHeader>
                <Typography variant="h2">{t('Inbox')}</Typography>
                <RefetchIndicator active={refetchInProgress} />
              </PageFrameHeader>
            }
            left={
              <Nav
                data={filteredData}
                pinnedItems={pinnedItems}
                g={selectedGroup}
                u={selectedUsers}
                t={selectedTemplates}
                topActions={
                  <InboxFilters
                    selectedUsers={selectedUsers}
                    selectedTemplates={selectedTemplates}
                    setSelectedUsers={setSelectedUsers}
                    setSelectedTemplates={setSelectedTemplates}
                    userOptions={userOptions}
                    tplOptions={tplOptions}
                    trackEventV2={trackEventV2}
                  />
                }
              />
            }
            center={
              <InboxMain
                data={filteredData}
                pinnedItems={pinnedItems}
                g={selectedGroup}
                setSelectedGroup={setSelectedGroup}
                mode={mode}
                returnTo={returnTo}
                linkToOverdueSection={linkToOverdueSection}
                u={selectedUsers}
                t={selectedTemplatesArray}
              />
            }
            right={null}
          />
        </PageContainerInner>
      </PageContainer>
    </>
  );
};

export const EmployeeAppPageInbox = ({
  userAccessToken,
  v
}: {
  userAccessToken: string;
  v: number;
}) => {
  const [data, , error] = useEmployeeAppInboxDataAndPinnedItemsAndUpdateDate(
    userAccessToken
  );
  useErrorLogger('useEmployeeAppInboxData', error);
  if (data) {
    return (
      <EmployeeAppPageInboxWithData
        data={data.inbox}
        pinnedItems={data.pinnedItems}
        refetchInProgress={data.refetchInProgress}
      />
    );
  }

  return <EmployeeAppPageLoader />;
};

const GroupNavForMessages = ({
  userAccessToken,
  selectedGroup,
  msgTitle,
  msgTemplateId,
  sortedMessages,
  returnTo,
  selectedInstanceAccessToken,
  sort,
  setSort
}: {
  userAccessToken: string;
  selectedGroup: InboxNavItemKey;
  msgTitle: string;
  msgTemplateId: string;
  tplId: string; // only used for pinning, which is currently not in use
  sortedMessages: IEmployeeAppInboxMessage[];
  returnTo: string;
  selectedInstanceAccessToken: string;
  sort: 'sentAt' | 'name';
  setSort: (sort: 'sentAt' | 'name') => void;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <NavContainer>
      <FlexContainerVertical spacing={2} fullWidth>
        <Link to={returnTo}>
          <Button
            size="small"
            variant="text"
            color="primary"
            startIcon={<CircleArrowLeft size={18} />}
          >
            {t('Show all messages')}
          </Button>
        </Link>
        <Typography variant="h5">
          <WithTokenPill d={msgTitle} />
        </Typography>
        <FlexContainer>
          <strong>{t('Sort by')}:</strong>
          <Select
            value={sort}
            onChange={(e) => setSort(e.target.value as 'sentAt' | 'name')}
          >
            <MenuItem value="sentAt">{t('Received')}</MenuItem>
            <MenuItem value="name">{t('First name')}</MenuItem>
          </Select>
        </FlexContainer>

        <FlexContainerVertical fullWidth spacing={0}>
          {sortedMessages.map((msg) => (
            <Link
              key={msg.instanceAccessToken}
              style={{ width: '100%' }}
              to={ROUTES_V2.inboxMessage.url(
                userAccessToken,
                msgTemplateId,
                msg.instanceAccessToken,
                {
                  g: selectedGroup,
                  sort
                }
              )}
            >
              <NavItemContainer
                selected={
                  selectedInstanceAccessToken === msg.instanceAccessToken
                }
              >
                <Typography variant="h6">
                  <FlexContainer fullWidth>
                    <strong>{toFullName(msg.participant)}</strong>
                    <div>
                      <PlainDate ts={msg.sendAt} />
                    </div>
                  </FlexContainer>
                </Typography>
              </NavItemContainer>
            </Link>
          ))}
        </FlexContainerVertical>
      </FlexContainerVertical>
    </NavContainer>
  );
};

const GroupNavForTasks = ({
  userAccessToken,
  selectedGroup,
  taskTitle,
  taskTemplateId,
  tplId,
  sortedTasks,
  returnTo,
  selectedInstanceAccessToken,
  sort,
  setSort
}: {
  userAccessToken: string;
  selectedGroup: InboxNavItemKey;
  taskTitle: string;
  taskTemplateId: string;
  tplId: string;
  sortedTasks: IEmployeeAppInboxTask[];
  returnTo: string;
  selectedInstanceAccessToken: string;
  sort: 'deadline' | 'name' | 'completion';
  setSort: (sort: 'deadline' | 'name' | 'completion') => void;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <NavContainer>
      <FlexContainerVertical spacing={2} fullWidth>
        <Link to={returnTo}>
          <Button
            size="small"
            variant="text"
            color="primary"
            startIcon={<CircleArrowLeft size={18} />}
          >
            {t('Show all tasks')}
          </Button>
        </Link>
        <Typography variant="h5">
          <WithTokenPill d={taskTitle} />
        </Typography>
        <FlexContainer>
          <PinButton
            userAccessToken={userAccessToken}
            type="TASK"
            templateId={tplId}
            itemId={taskTemplateId}
          />
        </FlexContainer>
        <FlexContainer>
          <strong>{t('Sort by')}:</strong>
          <Select
            value={sort}
            onChange={(e) =>
              setSort(e.target.value as 'deadline' | 'name' | 'completion')
            }
          >
            {selectedGroup === 'complete' ? (
              <MenuItem value="completion">{t('Completion')}</MenuItem>
            ) : (
              <MenuItem value="deadline">{t('Deadline')}</MenuItem>
            )}
            <MenuItem value="name">{t('First name')}</MenuItem>
          </Select>
        </FlexContainer>

        <FlexContainerVertical fullWidth spacing={0}>
          {sortedTasks.map((tsk) => (
            <Link
              key={tsk.instanceAccessToken}
              style={{ width: '100%' }}
              to={ROUTES_V2.inboxTask.url(
                userAccessToken,
                taskTemplateId,
                tsk.instanceAccessToken,
                {
                  g: selectedGroup,
                  sort
                }
              )}
            >
              <NavItemContainer
                selected={
                  selectedInstanceAccessToken === tsk.instanceAccessToken
                }
              >
                <Typography variant="h6">
                  <FlexContainer fullWidth>
                    <strong>{toFullName(tsk.participant)}</strong>
                    <div>
                      <DueOrCompletionDate
                        task={{
                          status: tsk.status,
                          completedAt: tsk.completedAt,
                          end: tsk.timing.end
                        }}
                        short
                      />
                    </div>
                  </FlexContainer>
                </Typography>
              </NavItemContainer>
            </Link>
          ))}
        </FlexContainerVertical>
      </FlexContainerVertical>
    </NavContainer>
  );
};

const EmployeeAppPageInboxTasksWithData = ({
  data,
  taskTemplateId
}: {
  data: IEmployeeAppInboxResponse;
  taskTemplateId: string;
}) => {
  useStandaloneAppThemeOverride(data.org.experienceTheming || null);
  const { history } = useRouter();

  // This just returns the loader - could also be null,
  // but most importantly: it navigates away from here
  // and either determines the best instance to go to,
  // or navigates to the main inbox page

  const [selectedGroup] = useNullableTypedStringQueryParam<InboxNavItemKey>(
    'g',
    null
  );
  const [selectedUsers] = useSelectedUsersForInbox();
  const [selectedTemplates] = useSelectedTemplatesForInbox();
  const [sort] = useTypedStringQueryParam<'deadline' | 'name' | 'completion'>(
    'sort',
    selectedGroup === 'complete' ? 'completion' : 'deadline'
  );
  const [tsort] = useStringQueryParam(TASK_GROUP_TABLE_SORT_QUERY_PARAM, '');

  const filteredData = useUserFilteredInboxData(
    data,
    selectedUsers,
    selectedTemplates
  );
  const taskTemplateData = useTaskTemplateData(
    filteredData,
    taskTemplateId,
    selectedGroup
  );
  const sortedTasks = useMemo(() => {
    return sortTasks(taskTemplateData?.tasks || [], sort);
  }, [taskTemplateData, sort]);

  // If there is no instance at all, go to the main page
  // If we have a group selected, find first instance within this group
  // If we have no group selected, just find the first instance in general
  //
  // The system also requires a good group to be selected, but this
  // will be determined either on the inbox or the task page.
  if (!taskTemplateData || !sortedTasks.length) {
    history.push(
      ROUTES_V2.inbox.url(data.userAccessToken, {
        g: selectedGroup || undefined
      })
    );
  } else {
    const firstTask = sortedTasks[0];
    history.push(
      ROUTES_V2.inboxTask.url(
        data.userAccessToken,
        taskTemplateId,
        firstTask.instanceAccessToken,
        {
          g: selectedGroup || undefined,
          tsort,
          sort,
          u: selectedUsers,
          t: [...selectedTemplates]
        }
      )
    );
  }

  return <EmployeeAppPageLoader />;
};

export const EmployeeAppPageInboxTasks = ({
  userAccessToken,
  taskTemplateId,
  v
}: {
  userAccessToken: string;
  taskTemplateId: string;
  v: number;
}) => {
  const [data, , error] = useEmployeeAppInboxDataAndPinnedItemsAndUpdateDate(
    userAccessToken
  );
  useErrorLogger('useEmployeeAppInboxTasksData', error);
  if (data) {
    return (
      <EmployeeAppPageInboxTasksWithData
        data={data.inbox}
        taskTemplateId={taskTemplateId}
      />
    );
  }

  return <EmployeeAppPageLoader />;
};

const useMessagesForCurrentGroup = (
  filteredData: IEmployeeAppInboxResponse,
  selectedGroup: InboxNavItemKey | null
) => {
  return useMemo(() => {
    return selectedGroup === 'archived-msgs'
      ? filteredData.messages.filter((msg) => msg.archived)
      : selectedGroup === 'msgs'
      ? filteredData.messages.filter((msg) => !msg.archived)
      : filteredData.messages;
  }, [filteredData, selectedGroup]);
};

const useMessageTemplateData = (
  filteredData: IEmployeeAppInboxResponse,
  msgTemplateId: string,
  selectedGroup: InboxNavItemKey | null
) => {
  const messages = useMessagesForCurrentGroup(filteredData, selectedGroup);
  return useMemo(() => {
    return (
      groupMessages(messages).find((d) => d.msgTemplateId === msgTemplateId) ||
      null
    );
  }, [messages, msgTemplateId]);
};

const EmployeeAppPageInboxMessagesWithData = ({
  data,
  msgTemplateId
}: {
  data: IEmployeeAppInboxResponse;
  msgTemplateId: string;
}) => {
  const { history } = useRouter();
  useStandaloneAppThemeOverride(data.org.experienceTheming || null);

  const [selectedGroup] = useNullableTypedStringQueryParam<InboxNavItemKey>(
    'g',
    'msgs'
  );
  const [sort] = useTypedStringQueryParam<'sentAt' | 'name'>('sort', 'sentAt');

  const [tsort] = useStringQueryParam(TASK_GROUP_TABLE_SORT_QUERY_PARAM, '');

  const [selectedUsers] = useSelectedUsersForInbox();
  const [selectedTemplates] = useSelectedTemplatesForInbox();

  const filteredData = useUserFilteredInboxData(
    data,
    selectedUsers,
    selectedTemplates
  );

  const msgTemplateData = useMessageTemplateData(
    filteredData,
    msgTemplateId,
    selectedGroup
  );

  const sortedMessages = useMemo(() => {
    return sortMessages(msgTemplateData?.messages || [], sort);
  }, [msgTemplateData, sort]);

  if (!msgTemplateData || !sortedMessages.length) {
    history.push(
      ROUTES_V2.inbox.url(data.userAccessToken, {
        g: selectedGroup || undefined
      })
    );
  } else {
    const firstMessage = sortedMessages[0];
    history.push(
      ROUTES_V2.inboxMessage.url(
        data.userAccessToken,
        msgTemplateId,
        firstMessage.instanceAccessToken,
        {
          g: selectedGroup || undefined,
          tsort,
          sort,
          u: selectedUsers,
          t: [...selectedTemplates]
        }
      )
    );
  }

  // const nextMsg = useMemo(() => {
  //   if (sortedMessages.length < 2) {
  //     return null;
  //   }

  //   const index = sortedMessages.findIndex(
  //     (msg) => msg.instanceAccessToken === selectedInstanceAccessToken
  //   );

  //   if (index === -1) {
  //     return null;
  //   }

  //   const next = sortedMessages[index + 1] || sortedMessages[0];

  //   // TODO - we also need to pass u and t here!
  //   return {
  //     label: `${t('Next person')}: ${toFullName(next.participant)}`,
  //     url: ROUTES_V2.inboxMessages.url(data.userAccessToken, msgTemplateId, {
  //       g: selectedGroup,
  //       i: next.instanceAccessToken
  //     }),
  //     tokens: {
  //       userAccessToken: data.userAccessToken,
  //       instanceAccessToken: next.instanceAccessToken
  //     }
  //   };
  // }, [
  //   selectedInstanceAccessToken,
  //   sortedMessages,
  //   data.userAccessToken,
  //   msgTemplateId,
  //   selectedGroup,
  //   t
  // ]);

  // const seeEverythingUrl = getInboxUrl(data.userAccessToken, {
  //   u: [instance?.data.participant.publicId || ''],
  //   t: [msgTemplateData?.templateId || '']
  // });

  // if (!msgTemplateData) {
  //   return <div>TODO No messages data found</div>;
  // }

  return <EmployeeAppPageLoader />;
};

export const EmployeeAppPageInboxMessages = ({
  userAccessToken,
  msgTemplateId,
  v
}: {
  userAccessToken: string;
  msgTemplateId: string;
  v: number;
}) => {
  const [data, , error] = useEmployeeAppInboxDataAndPinnedItemsAndUpdateDate(
    userAccessToken
  );
  useErrorLogger('useEmployeeAppInboxMessagesData', error);
  if (data) {
    return (
      <EmployeeAppPageInboxMessagesWithData
        data={data.inbox}
        msgTemplateId={msgTemplateId}
      />
    );
  }

  return <EmployeeAppPageLoader />;
};

const DisabledFilterTooltip = () => {
  const { t } = useUnsafeTranslation();
  return (
    <FlexContainerVertical spacing={0}>
      <div>{t('Filters can only be applied on a list of tasks/messages.')}</div>
      <div>{t('Return to a list page to change your filters.')}</div>
    </FlexContainerVertical>
  );
};

const useTasksForCurrentGroup = (
  filteredData: IEmployeeAppInboxResponse,
  selectedGroup: InboxNavItemKey | null
) => {
  return useMemo(() => {
    const getTasksWithStatus = (statuses: TaskStatus[] | null) =>
      statuses === null
        ? filteredData.tasks
        : filteredData.tasks.filter((x) => statuses.includes(x.status));
    const tasks = getTasksWithStatus(
      selectedGroup === 'overdue'
        ? [TaskStatus.LATE]
        : selectedGroup === 'todo'
        ? [TaskStatus.RUNNING]
        : selectedGroup === 'complete'
        ? [TaskStatus.COMPLETE]
        : selectedGroup === 'optional'
        ? [TaskStatus.PAST_DEADLINE]
        : selectedGroup === 'upcoming'
        ? [TaskStatus.NOT_STARTED]
        : selectedGroup === 'everything'
        ? [TaskStatus.RUNNING, TaskStatus.LATE]
        : null
    );
    return tasks;
  }, [filteredData, selectedGroup]);
};

const useTaskTemplateData = (
  filteredData: IEmployeeAppInboxResponse,
  taskTemplateId: string,
  selectedGroup: InboxNavItemKey | null
) => {
  const tasks = useTasksForCurrentGroup(filteredData, selectedGroup);
  return useMemo(() => {
    return (
      groupTasks(tasks).find((d) => d.taskTemplateId === taskTemplateId) || null
    );
  }, [tasks, taskTemplateId]);
};

const EmployeeAppPageInboxTaskWithData = ({
  data,
  pinnedItems,
  taskTemplateId,
  instanceAccessToken,
  instanceLv,
  refetchInProgress
}: {
  data: IEmployeeAppInboxResponse;
  pinnedItems: IEmployeeAppPinnedItemsResponse['items'];
  taskTemplateId: string;
  instanceAccessToken: string;
  instanceLv: LoadingValue<Doc<IExperienceDenormalizedV2>>;
  refetchInProgress: boolean;
}) => {
  // if no instance id is provided, then get the first instance for which we have
  // data
  // if there is none -> return to main inbox
  // before this collect the correct group
  // before this collect the right set of tasks based on this group
  // if there is only one, show the normal nav
  // if there are several, show the other nav
  // next button either moves inside the group, or to the next group

  // If there is no group, go ahead and compute everything.
  // If there is only a single instance around anyway, then just render.
  // If there is more than one, try to find the right group for this instance.
  // And redirect to that state.

  const { t } = useUnsafeTranslation();
  useStandaloneAppThemeOverride(data.org.experienceTheming || null);
  useTrackCombinedInboxPageLoadedEvent(data.userAccessToken);
  const { trackEventV2 } = useProductAnalyticsV2(data.userAccessToken);

  const [
    selectedGroup,
    setSelectedGroup
  ] = useNullableTypedStringQueryParam<InboxNavItemKey>('g', null);
  const [selectedUsers] = useSelectedUsersForInbox();
  const [selectedTemplates] = useSelectedTemplatesForInbox();

  const [tableSortParam] = useStringQueryParam(
    TASK_GROUP_TABLE_SORT_QUERY_PARAM,
    ''
  );

  const [returnTo] = useStringQueryParam(
    'returnTo',
    getInboxUrl(data.userAccessToken, {
      g: selectedGroup || undefined,
      u: selectedUsers,
      t: [...selectedTemplates],
      tsort: tableSortParam
    })
  );

  const [instance] = instanceLv;

  const seeEverythingUrl = getInboxUrl(data.userAccessToken, {
    u: [instance?.data.participant.publicId || ''],
    t: [
      data.tasks.find((tsk) => tsk.taskTemplateId === taskTemplateId)
        ?.templateId || ''
    ]
  });

  // Based on request comments, we could hold this data stable
  // until we choose to show updates to the user.
  // This might be the simplest way.
  // We just don't let *data* change and hold the previous state
  // in a ref, until we're ready to give out the new data.
  const filteredData = useUserFilteredInboxData(
    data,
    selectedUsers,
    selectedTemplates
  );

  const filterOptions = useFilterOptionsForInbox(data);

  const [sort, setSort] = useTypedStringQueryParam<
    'deadline' | 'name' | 'completion'
  >('sort', selectedGroup === 'complete' ? 'completion' : 'deadline');

  const taskTemplateData = useTaskTemplateData(
    filteredData,
    taskTemplateId,
    selectedGroup
  );
  const sortedTasks = useMemo(() => {
    return sortTasks(taskTemplateData?.tasks || [], sort);
  }, [taskTemplateData, sort]);

  // Compute nextButtons from the live `filteredData` — NOT a snapshot. An
  // earlier version held filteredData in a useRef and only refreshed it when
  // instanceAccessToken or taskTemplateId changed; the intent was to "hold
  // data stable between navigations" but the side effect was that completing
  // a subtask (which mutates task.status / completedAt via the Firestore
  // listener) never reached the nextButtons calculation. That caused:
  //   - the Next button pointing at a now-completed task
  //   - the task not visibly clearing until the user navigated away and back
  //   - the per-participant left nav flickering on completion
  // Drop the snapshot. If we ever want to smooth flicker again, do it inside
  // the listener layer (e.g. debounce filteredData), not by freezing a ref.
  const nextButtons = useNextButtonDefForInboxTask(
    filteredData,
    instanceAccessToken,
    taskTemplateId,
    selectedGroup,
    selectedUsers,
    selectedTemplates,
    pinnedItems,
    tableSortParam,
    sort,
    returnTo
  );

  if (!selectedGroup) {
    // determine a good group
    const currentTask = sortedTasks.find(
      (x) => x.instanceAccessToken === instanceAccessToken
    );
    if (!currentTask) {
      // We don't really know where we are
      setSelectedGroup('everything');
    } else if (currentTask.status === TaskStatus.COMPLETE) {
      setSelectedGroup('complete');
    } else if (currentTask.status === TaskStatus.LATE) {
      setSelectedGroup('overdue');
    } else if (currentTask.status === TaskStatus.RUNNING) {
      setSelectedGroup('todo');
    } else if (currentTask.status === TaskStatus.NOT_STARTED) {
      setSelectedGroup('upcoming');
    } else if (currentTask.status === TaskStatus.PAST_DEADLINE) {
      setSelectedGroup('optional');
    } else {
      setSelectedGroup('everything');
    }
    return <EmployeeAppPageLoader />;
  }

  return (
    <>
      <ReactHelmet>
        <title>{t('Inbox')}</title>
      </ReactHelmet>

      <PageContainer>
        <PageContainerInner>
          <TopNavBarV2ForExperienceHub
            data={data}
            linkInterpolationData={
              instance
                ? {
                    templateId: instance.data.templateId,
                    instanceId: instance.data.expId
                  }
                : null
            }
            searchComponent={<SearchWithinInboxButton data={data} />}
          />
          <PageFrame
            aiDisclosure={data.aiDisclosure}
            header={
              <PageFrameHeader>
                <Typography variant="h2">{t('Inbox')}</Typography>
                <RefetchIndicator active={refetchInProgress} />
              </PageFrameHeader>
            }
            left={
              sortedTasks.length > 1 && taskTemplateData ? (
                <GroupNavForTasks
                  userAccessToken={data.userAccessToken}
                  selectedGroup={selectedGroup}
                  taskTitle={taskTemplateData.taskTitle}
                  taskTemplateId={taskTemplateId}
                  tplId={taskTemplateData.templateId}
                  sortedTasks={sortedTasks}
                  sort={sort}
                  setSort={setSort}
                  returnTo={returnTo}
                  selectedInstanceAccessToken={instanceAccessToken}
                />
              ) : (
                <Nav
                  data={filteredData}
                  pinnedItems={pinnedItems}
                  g={selectedGroup}
                  u={selectedUsers}
                  t={selectedTemplates}
                  topActions={
                    <InboxFilters
                      disabled
                      selectedUsers={selectedUsers}
                      selectedTemplates={selectedTemplates}
                      setSelectedUsers={NOOP}
                      setSelectedTemplates={NOOP}
                      {...filterOptions}
                      disabledTooltip={<DisabledFilterTooltip />}
                      trackEventV2={trackEventV2}
                    />
                  }
                />
              )
            }
            center={
              instance ? (
                <TaskDetails
                  data={data}
                  taskTemplateId={taskTemplateId}
                  instance={instance}
                  returnTo={returnTo}
                  showTemplateTitle
                  additionalLinkInHeader={{
                    label: t('See everything for this experience'),
                    url: seeEverythingUrl
                  }}
                  nextButtons={nextButtons}
                />
              ) : (
                <Loader height={500} />
              )
            }
            right={null}
          />
        </PageContainerInner>
      </PageContainer>

      {nextButtons.map((nextButton, i) => (
        <Fragment key={i}>
          {nextButton.preload && (
            <InstancePreloadSink
              userAccessToken={nextButton.preload.userAccessToken}
              instanceAccessToken={nextButton.preload.instanceAccessToken}
            />
          )}
        </Fragment>
      ))}
    </>
  );
};

export const EmployeeAppPageInboxTask = ({
  userAccessToken,
  taskTemplateId,
  instanceAccessToken,
  v
}: {
  userAccessToken: string;
  taskTemplateId: string;
  instanceAccessToken: string;
  v: number;
}) => {
  const [
    data,
    loading,
    error
  ] = useEmployeeAppInboxDataAndPinnedItemsAndUpdateDate(userAccessToken);
  const instanceLv = useEmployeeAppExperienceInstance(
    userAccessToken,
    instanceAccessToken,
    v
  );

  useErrorLogger('useEmployeeAppInboxTasksData', error);
  if (data) {
    return (
      <EmployeeAppPageInboxTaskWithData
        data={data.inbox}
        pinnedItems={data.pinnedItems}
        taskTemplateId={taskTemplateId}
        instanceAccessToken={instanceAccessToken}
        instanceLv={instanceLv}
        refetchInProgress={data.refetchInProgress}
      />
    );
  }

  if (loading) {
    return <EmployeeAppPageLoader />;
  }

  if (error) {
    return (
      <SomethingWentWrongWithReport
        height={300}
        error={error}
        context={{ page: 'standalone_inbox' }}
      />
    );
  }

  return null;
};

const EmployeeAppPageInboxMessageWithData = ({
  data,
  pinnedItems,
  msgTemplateId,
  instanceAccessToken,
  instanceLv,
  refetchInProgress
}: {
  data: IEmployeeAppInboxResponse;
  pinnedItems: IEmployeeAppPinnedItemsResponse['items'];
  msgTemplateId: string;
  instanceAccessToken: string;
  instanceLv: LoadingValue<Doc<IExperienceDenormalizedV2>>;
  refetchInProgress: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  useStandaloneAppThemeOverride(data.org.experienceTheming || null);
  useTrackCombinedInboxPageLoadedEvent(data.userAccessToken);
  const { trackEventV2 } = useProductAnalyticsV2(data.userAccessToken);
  const [
    selectedGroup,
    setSelectedGroup
  ] = useNullableTypedStringQueryParam<InboxNavItemKey>('g', 'msgs');
  const [selectedUsers] = useSelectedUsersForInbox();
  const [selectedTemplates] = useSelectedTemplatesForInbox();

  const [sort, setSort] = useTypedStringQueryParam<'sentAt' | 'name'>(
    'sort',
    'sentAt'
  );

  const [tableSortParam] = useStringQueryParam(
    TASK_GROUP_TABLE_SORT_QUERY_PARAM,
    ''
  );

  const [returnTo] = useStringQueryParam(
    'returnTo',
    getInboxUrl(data.userAccessToken, {
      g: selectedGroup || undefined,
      u: selectedUsers,
      t: [...selectedTemplates],
      tsort: tableSortParam
    })
  );

  const [instance] = instanceLv;

  const seeEverythingUrl = getInboxUrl(data.userAccessToken, {
    u: [instance?.data.participant.publicId || ''],
    t: [msgTemplateId]
  });

  const filteredData = useUserFilteredInboxData(
    data,
    selectedUsers,
    selectedTemplates
  );

  const msgTemplateData = useMessageTemplateData(
    filteredData,
    msgTemplateId,
    selectedGroup
  );

  const sortedMessages = useMemo(() => {
    return sortMessages(msgTemplateData?.messages || [], sort);
  }, [msgTemplateData, sort]);

  const filterOptions = useFilterOptionsForInbox(data);

  // Use live `filteredData` directly — see comment on the tasks-equivalent
  // computation above. The previous useRef snapshot held data stale between
  // navigations, which broke "Next message" targeting once read/archived
  // state changed under the listener.
  const nextButtons = useNextButtonDefForInboxMessage(
    filteredData,
    instanceAccessToken,
    msgTemplateId,
    selectedGroup,
    selectedUsers,
    selectedTemplates,
    pinnedItems,
    tableSortParam,
    sort,
    returnTo
  );

  if (!selectedGroup) {
    const currentMessage = sortedMessages.find(
      (msg) => msg.instanceAccessToken === instanceAccessToken
    );
    if (currentMessage?.archived) {
      setSelectedGroup('archived-msgs');
    } else {
      setSelectedGroup('msgs');
    }
    // Find the best group and select it
    return <EmployeeAppPageLoader />;
  }

  return (
    <>
      <ReactHelmet>
        <title>{t('Inbox')}</title>
      </ReactHelmet>

      <PageContainer>
        <PageContainerInner>
          <TopNavBarV2ForExperienceHub
            data={data}
            linkInterpolationData={
              instance
                ? {
                    templateId: instance.data.templateId,
                    instanceId: instance.data.expId
                  }
                : null
            }
            searchComponent={<SearchWithinInboxButton data={data} />}
          />
          <PageFrame
            aiDisclosure={data.aiDisclosure}
            header={
              <PageFrameHeader>
                <Typography variant="h2">{t('Inbox')}</Typography>
                <RefetchIndicator active={refetchInProgress} />
              </PageFrameHeader>
            }
            left={
              sortedMessages.length > 1 && msgTemplateData ? (
                <GroupNavForMessages
                  userAccessToken={data.userAccessToken}
                  selectedGroup={selectedGroup}
                  msgTitle={msgTemplateData.msgTitle}
                  msgTemplateId={msgTemplateId}
                  tplId={msgTemplateData.templateId}
                  sortedMessages={sortedMessages}
                  sort={sort}
                  setSort={setSort}
                  returnTo={returnTo}
                  selectedInstanceAccessToken={instanceAccessToken}
                />
              ) : (
                <Nav
                  data={filteredData}
                  pinnedItems={pinnedItems}
                  g={selectedGroup}
                  u={selectedUsers}
                  t={selectedTemplates}
                  topActions={
                    <InboxFilters
                      disabled
                      selectedUsers={selectedUsers}
                      selectedTemplates={selectedTemplates}
                      setSelectedUsers={NOOP}
                      setSelectedTemplates={NOOP}
                      disabledTooltip={<DisabledFilterTooltip />}
                      {...filterOptions}
                      trackEventV2={trackEventV2}
                    />
                  }
                />
              )
            }
            center={
              instance ? (
                <MessageDetails
                  data={data}
                  msgTemplateId={msgTemplateId}
                  instance={instance}
                  returnTo={returnTo}
                  showTemplateTitle
                  additionalLinkInHeader={{
                    label: t('See everything for this experience'),
                    url: seeEverythingUrl
                  }}
                  nextButtons={nextButtons}
                />
              ) : (
                <Loader height={500} />
              )
            }
            right={null}
          />
        </PageContainerInner>
      </PageContainer>

      {nextButtons.map((nextButton, i) => (
        <Fragment key={i}>
          {nextButton.preload && (
            <InstancePreloadSink
              userAccessToken={nextButton.preload.userAccessToken}
              instanceAccessToken={nextButton.preload.instanceAccessToken}
            />
          )}
        </Fragment>
      ))}
    </>
  );
};

export const EmployeeAppPageInboxMessage = ({
  userAccessToken,
  msgTemplateId,
  instanceAccessToken,
  v
}: {
  userAccessToken: string;
  msgTemplateId: string;
  instanceAccessToken: string;
  v: number;
}) => {
  const [
    data,
    loading,
    error
  ] = useEmployeeAppInboxDataAndPinnedItemsAndUpdateDate(userAccessToken);
  const instanceLv = useEmployeeAppExperienceInstance(
    userAccessToken,
    instanceAccessToken,
    v
  );

  useErrorLogger('useEmployeeAppInboxMessagesData', error);
  if (data) {
    return (
      <EmployeeAppPageInboxMessageWithData
        data={data.inbox}
        pinnedItems={data.pinnedItems}
        msgTemplateId={msgTemplateId}
        instanceAccessToken={instanceAccessToken}
        instanceLv={instanceLv}
        refetchInProgress={data.refetchInProgress}
      />
    );
  }

  if (loading) {
    return <EmployeeAppPageLoader />;
  }

  if (error) {
    return (
      <SomethingWentWrongWithReport
        height={300}
        error={error}
        context={{ page: 'standalone_inbox', section: 'task_data' }}
      />
    );
  }

  return null;
};
const EmployeeAppPageInboxMomentWithData = ({
  data,
  pinnedItems,
  momentInstanceAccessToken,
  momentLv,
  refetchInProgress
}: {
  data: IEmployeeAppInboxResponse;
  pinnedItems: IEmployeeAppPinnedItemsResponse['items'];
  momentInstanceAccessToken: string;
  momentLv: LoadingValue<Doc<IMomentDenormalized>>;
  refetchInProgress: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  useStandaloneAppThemeOverride(data.org.experienceTheming || null);
  useTrackCombinedInboxPageLoadedEvent(data.userAccessToken);
  const { trackEventV2 } = useProductAnalyticsV2(data.userAccessToken);
  // Auto determine the selected group based on the moment
  const [
    selectedGroup,
    setSelectedGroup
  ] = useNullableTypedStringQueryParam<InboxNavItemKey>('g', 'moments-active');
  const [selectedUsers] = useSelectedUsersForInbox();
  const [selectedTemplates] = useSelectedTemplatesForInbox();

  const [tableSortParam] = useStringQueryParam(
    TASK_GROUP_TABLE_SORT_QUERY_PARAM,
    ''
  );

  const [returnTo] = useStringQueryParam(
    'returnTo',
    getInboxUrl(data.userAccessToken, {
      g: selectedGroup || undefined,
      u: selectedUsers,
      t: [...selectedTemplates],
      tsort: tableSortParam
    })
  );

  const [mom] = momentLv;

  // Use live `data` directly — see comment on the tasks-equivalent
  // computation above. The previous useRef snapshot held data stale between
  // navigations, which broke "Next moment" targeting once read/archived
  // state changed under the listener.
  const nextButtons = useNextButtonDefForInboxMoment(
    data,
    momentInstanceAccessToken,
    selectedGroup,
    selectedUsers,
    selectedTemplates,
    pinnedItems,
    tableSortParam,
    returnTo,
    !!selectedGroup && selectedGroup !== 'moments-archived'
  );

  if (
    !selectedGroup ||
    !['moments-active', 'moments-archived'].includes(selectedGroup)
  ) {
    // determine based on requested moment
    const selectedMoment = data.moments.find(
      (m) => m.momentAccessToken === momentInstanceAccessToken
    );
    if (selectedMoment?.markedAsRead || selectedMoment?.archivedBasedOnTime) {
      setSelectedGroup('moments-archived');
    } else {
      setSelectedGroup('moments-active');
    }
    return <EmployeeAppPageLoader />;
  }

  return (
    <>
      <ReactHelmet>
        <title>{t('Inbox')}</title>
      </ReactHelmet>

      <PageContainer>
        <PageContainerInner>
          <TopNavBarV2ForExperienceHub
            data={data}
            linkInterpolationData={
              // Admin app button - not yet implemented
              null
            }
            searchComponent={<SearchWithinInboxButton data={data} />}
          />

          <PageFrame
            aiDisclosure={data.aiDisclosure}
            header={
              <PageFrameHeader>
                <Typography variant="h2">{t('Inbox')}</Typography>
                <RefetchIndicator active={refetchInProgress} />
              </PageFrameHeader>
            }
            left={
              <Nav
                data={data}
                pinnedItems={pinnedItems}
                g={selectedGroup}
                u={selectedUsers}
                t={selectedTemplates}
              />
            }
            center={
              mom ? (
                <InboxMomentDetails
                  instance={mom}
                  actorAccessToken={data.userAccessToken}
                  momentInstanceAccessToken={momentInstanceAccessToken}
                  returnTo={returnTo}
                  noBackButton={false}
                  additionalLinkInHeader={null}
                  nextButtons={
                    // difficult to compute for archived moments - let's just leave them out for now
                    selectedGroup === 'moments-archived'
                      ? EMPTY_ARR
                      : nextButtons
                  }
                />
              ) : (
                <Loader height={500} />
              )
            }
            right={null}
          />
        </PageContainerInner>
      </PageContainer>

      {nextButtons.map((nextButton, i) => (
        <Fragment key={i}>
          {nextButton.preload && (
            <MomentPreloadSink
              userAccessToken={nextButton.preload.userAccessToken}
              momentInstanceAccessToken={nextButton.preload.instanceAccessToken}
            />
          )}
        </Fragment>
      ))}
    </>
  );
};

export const EmployeeAppPageInboxMoment = ({
  userAccessToken,
  momentInstanceAccessToken,
  v
}: {
  userAccessToken: string;
  momentInstanceAccessToken: string;
  v: number;
}) => {
  const [
    data,
    loading,
    error
  ] = useEmployeeAppInboxDataAndPinnedItemsAndUpdateDate(userAccessToken);
  const momentLv = useEmployeeAppMomentInstance(
    userAccessToken,
    momentInstanceAccessToken,
    v
  );

  useErrorLogger('useEmployeeAppInboxMomentData', error);
  if (data) {
    return (
      <EmployeeAppPageInboxMomentWithData
        data={data.inbox}
        pinnedItems={data.pinnedItems}
        momentInstanceAccessToken={momentInstanceAccessToken}
        momentLv={momentLv}
        refetchInProgress={data.refetchInProgress}
      />
    );
  }

  if (loading) {
    return <EmployeeAppPageLoader />;
  }

  if (error) {
    return (
      <SomethingWentWrongWithReport
        height={300}
        error={error}
        context={{ page: 'standalone_inbox', section: 'moment_data' }}
      />
    );
  }

  return null;
};

// Moments table page
// Same strategy as others, selected the correct group based on the moment first
// Endpoint to mark as read
